// 9fbef606107a605d69c0edbcd8029e5d

import React from 'react';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import Router from 'next/router';
import isEqual from 'lodash/isEqual';
import { getViewportInfo } from '@tcp/core/src/utils';
import Image from '@tcp/core/src/components/common/atoms/Image';
import {
  GLOBAL_ACCESSIBILITY_LABELS,
  GlobalAccessibilityLabels,
} from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';

import config from '../Carousel.config';
import { getImageFilePath, getIconPath } from '../../../../../utils';
import CarouselStyle from '../Carousel.style';
import withStyles from '../../../hoc/withStyles';
import errorBoundary from '../../../hoc/withErrorBoundary';

const defaults = { ...config.CAROUSEL_DEFAULTS };
const defaultsUnmount = { ...config.CAROUSEL_DEFAULTS_UNMOUNT };

/**
 * @function Carousel component that creates carousel using
 * third party 'react-slick'
 */
class Carousel extends React.Component {
  constructor(props) {
    super(props);
    this.slider = null;
    this.carouselRef = React.createRef();
    this.getSlider = this.getSlider.bind(this);
    this.getPlayButton = this.getPlayButton.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.productsFired = [];
    this.state = {
      autoplay: true,
      uniqueId: Math.random() * 1000,
      // uniqueId is generated when a new carousel is instantiated and is passed to slider to keep them unique
      // key could also be passed from the module from the component(like in moduleH) which will overwrite this key
      loopCompleted: 0,
      hasMounted: false,
    };
    this.accessibilityLabels = getLabelFromCopyOrFromTranslation(
      GLOBAL_ACCESSIBILITY_LABELS,
      GlobalAccessibilityLabels
    );

    this.previousButton = renderLabel(this.accessibilityLabels.previousButton, 'previousButton');
    this.nextIconButton = renderLabel(this.accessibilityLabels.nextIconButton, 'nextIconButton');
    this.nextArrowElement = <button type="button" aria-label={this.nextIconButton} />;
    this.prevArrowElement = <button type="button" aria-label={this.previousButton} />;
  }

  componentDidMount() {
    const {
      options: { maxLoopCount },
      selectedSlideIndex,
    } = this.props;
    if (maxLoopCount) {
      Router.events.on('routeChangeComplete', this.resetLoopCount);
    }
    if (selectedSlideIndex !== undefined) {
      this.slider.slickGoTo(selectedSlideIndex, true);
    }
    this.setState({
      hasMounted: true,
    });
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (isEqual(nextProps, this.props) && isEqual(nextState, this.state)) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const {
      isMountedHideLeftNavigationOnPLPEnabled: prevIsMountedHideLeftNavigationOnPLPEnabled,
      itemsLength: prevItemsLength,
    } = prevProps;
    const {
      fromOutfitCarouselModule,
      isMountedHideLeftNavigationOnPLPEnabled,
      selectedSlideIndex,
      itemsLength,
      fromBundleSummary,
    } = this.props;
    if (
      fromOutfitCarouselModule &&
      prevIsMountedHideLeftNavigationOnPLPEnabled !== isMountedHideLeftNavigationOnPLPEnabled &&
      selectedSlideIndex !== undefined
    ) {
      this.slider.innerSlider?.changeSlide({ message: 'index', index: selectedSlideIndex }, true);
    }

    if (
      fromBundleSummary &&
      ((prevItemsLength === 4 && itemsLength === 3) ||
        (itemsLength > 3 && itemsLength === prevItemsLength + 1))
    ) {
      this.slider.innerSlider?.changeSlide({ message: 'index', index: 0 }, true);
    }
  }

  componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.resetLoopCount);
  }

  getPlayIcon = () => {
    const { isModule } = this.props;
    return isModule ? `${getImageFilePath()}/play-accessible.svg` : getIconPath('icon-pause');
  };

  getPauseIcon = () => {
    const { isModule } = this.props;
    return isModule ? `${getImageFilePath()}/pause-accessible.svg` : getIconPath('icon-play');
  };

  /**
   * @function getSlider function gets DOM reference of slider component.
   * @param {[Object]} element [Event object of click].
   * @return {node} function returns slider element.
   */
  getSlider(element) {
    this.slider = element;
    return this.slider;
  }

  /**
   * @function getPlayComponent function gets DOM reference of slider component.
   * @param {[Object]} element [Event object of click].
   * @return {node} function returns slider element.
   */
  getPlayButton(wrapperConfig) {
    const { autoplay } = this.state;
    const { playIconButtonLabel, pauseIconButtonLabel, dataLocatorPause, dataLocatorPlay } =
      wrapperConfig;

    const buttonClass = 'tcp_carousel__play_pause_button';
    return autoplay ? (
      <button
        className={buttonClass}
        data-locator={dataLocatorPause}
        onClick={this.pause}
        aria-label={pauseIconButtonLabel}
      >
        <Image
          className="tcp_carousel__play_pause_button_icon"
          aria-hidden="true"
          src={this.getPauseIcon()}
        />
      </button>
    ) : (
      <button
        className={buttonClass}
        data-locator={dataLocatorPlay}
        onClick={this.play}
        aria-label={playIconButtonLabel}
      >
        <Image
          className="tcp_carousel__play_pause_button_icon"
          aria-hidden="true"
          src={this.getPlayIcon()}
        />
      </button>
    );
  }

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    const { sliderImageIndex, accessoryProductId } = nextProps;
    const { sliderImageIndex: sliderImage, accessoryProductId: accessoryProductIdCurrentProps } =
      this.props;
    if (
      sliderImageIndex !== sliderImage ||
      (accessoryProductIdCurrentProps &&
        sliderImageIndex === sliderImage &&
        accessoryProductId !== accessoryProductIdCurrentProps)
    ) {
      this.slider.slickGoTo(Math.floor(sliderImageIndex));
    }
  };

  resetLoopCount = () => {
    const {
      options: { maxLoopCount },
    } = this.props;

    if (maxLoopCount) {
      this.setState({
        loopCompleted: 0,
        autoplay: true,
      });
      if (this.slider) {
        this.slider.slickPlay();
      }
    }
  };

  appendDots = (dots) => {
    const { carouselConfig, options, isPlpQV } = this.props;
    const { hasMounted } = this.state;
    const { isMobile } = (hasMounted && getViewportInfo()) || {};

    return (
      <div>
        {/*
          carouselConfig.autoplay has been used to show/hide play icon only, the auto slide will
          still work. However, the options.autoplay has been used to stop the auto sliding of the carousel.
         */}
        {carouselConfig.autoplay && options.autoplay && this.getPlayButton(carouselConfig)}
        {isPlpQV ? options.dots && isMobile && <ul>{dots}</ul> : options.dots && <ul>{dots}</ul>}
      </div>
    );
  };

  handleSlideChange = (currentSlide) => {
    const { loadSendEventsToMonetate, recTokens } = this.props;
    if (this.slider) {
      const slick = this.slider;
      const { slidesToShow } = slick.props || {};
      const slideCount = slick.props.children?.length;
      const indexStarting =
        currentSlide + slidesToShow > slideCount ? slideCount - currentSlide : currentSlide;

      const visibleIndices = [];
      for (let i = 0; i < slidesToShow; i += 1) {
        const index = (indexStarting + i) % slideCount;
        if (index >= slidesToShow && !this.productsFired.includes(index)) {
          this.productsFired.push(index);
          visibleIndices.push(index);
        }
      }
      if (visibleIndices.length) {
        const itemsInView = recTokens.slice(
          visibleIndices[0],
          visibleIndices[visibleIndices.length - 1] + 1
        );

        if (itemsInView?.length > 0)
          loadSendEventsToMonetate({
            recToken: itemsInView,
          });
      }
    }
  };

  /**
   * @function focusCurrentSlide focuses the current slide element for accessibility
   * @param {number} currentIndex - The index of the current slide
   */
  focusCurrentSlide = () => {
    if (this.carouselRef.current) {
      const currentSlideElement = this.carouselRef.current.querySelector(
        '.slick-current .recommendation-tile'
      );

      if (currentSlideElement) {
        if (!currentSlideElement.hasAttribute('tabindex')) {
          currentSlideElement.setAttribute('tabindex', '-1');
        }

        currentSlideElement.focus();
      }
    }
  };

  /**
   * @function afterChange pause autoplay after max loop count completed
   */
  afterChange = (i) => {
    const {
      options: { maxLoopCount },
      onCarouselSlide,
      recTokens,
      fromDyRecommendations,
    } = this.props;
    if (recTokens?.length > 0) this.handleSlideChange(i);
    if (onCarouselSlide) onCarouselSlide();

    if (fromDyRecommendations) {
      this.focusCurrentSlide(i);
    }

    const { autoplay } = this.state;
    let { loopCompleted } = this.state;
    if (maxLoopCount && autoplay) {
      if (loopCompleted >= maxLoopCount) {
        this.pause();
        this.setState({
          autoplay: false,
        });
        return;
      }
      if (this.slider.props.children && i === this.slider.props.children.length - 1) {
        loopCompleted += 1;
        this.setState({
          loopCompleted,
        });
      }
    }
  };

  /**
   * @function updateState function updates component state
   * after each click on play pause button.
   */
  togglePlay() {
    const { autoplay } = this.state;
    this.setState({ autoplay: !autoplay });
  }

  /**
   * @function pause function stops/pause autoplay for carousel
   * also update component state.
   */
  pause() {
    this.slider.slickPause();
    this.togglePlay();
  }

  /**
   * @function play function enable autoplay for carousel
   * also update component state.
   */
  play() {
    this.slider.slickPlay();
    this.togglePlay();
  }

  /**
   * @function render  Used to render the JSX of the component
   * @param {object} options Customized caroused configs from parent wrapper
   * @param {node} children address object
   * @param {object} carouselConfig Carousel wrapper config to enable customization
   * of functionalities like play pause, change carousel theme etc.
   */
  render() {
    const { hasMounted } = this.state;
    const { options, children, carouselConfig, className, mountedSetting } = this.props;
    const { maxLoopCount, ...otherOptions } = options;

    const initialSetting = {
      appendDots: this.appendDots,
      prevArrow: this.prevArrowElement,
      nextArrow: this.nextArrowElement,
      /*
         The dots will be created on both cases. we need this as we are putting custom play/pause
         inside the slick-dots container. So, if some cases if dots not required and we will be able
         render play/pause button or vice-versa. Also check this.getPlayButton()
        */
      dots: options.dots || options.autoplay,
    };
    let settings;

    if (mountedSetting) {
      if (hasMounted) {
        settings = { ...initialSetting, ...defaults, ...otherOptions };
      } else {
        const otherOptionsBeforeMounted = {
          ...otherOptions,
          responsive: otherOptions?.responsive?.map(({ settings: defaultSetting }) => ({
            settings: defaultSetting,
          })),
        };
        settings = { ...initialSetting, ...defaultsUnmount, ...otherOptionsBeforeMounted };
      }
    } else {
      settings = { ...initialSetting, ...defaults, ...otherOptions };
    }

    const { uniqueId } = this.state;

    return (
      <div
        ref={this.carouselRef}
        className={`${className} tcp_carousel_wrapper`}
        data-locator={carouselConfig.dataLocatorCarousel}
      >
        <Slider
          className="tcp_carousel"
          ref={this.getSlider}
          key={uniqueId}
          afterChange={this.afterChange}
          {...settings}
        >
          {!children ? null : children}
        </Slider>
      </div>
    );
  }
}

Carousel.defaultProps = {
  isModule: false,
  isPlpQV: false,
  playIconButtonLabel: '',
  pauseIconButtonLabel: '',
  sliderImageIndex: 0,
  onCarouselSlide: () => {},
  isNoResponseRecommendation: false,
  selectedSlideIndex: undefined,
  mountedSetting: false,
  fromOutfitCarouselModule: false,
  isMountedHideLeftNavigationOnPLPEnabled: false,
};

Carousel.propTypes = {
  options: PropTypes.shape({}).isRequired,
  children: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  carouselConfig: PropTypes.shape({}).isRequired,
  className: PropTypes.string.isRequired,
  playIconButtonLabel: PropTypes.string,
  pauseIconButtonLabel: PropTypes.string,
  sliderImageIndex: PropTypes.number,
  isModule: PropTypes.bool,
  isPlpQV: PropTypes.bool,
  onCarouselSlide: PropTypes.func,
  isNoResponseRecommendation: PropTypes.bool,
  selectedSlideIndex: PropTypes.number,
  mountedSetting: PropTypes.bool,
  fromOutfitCarouselModule: PropTypes.bool,
  isMountedHideLeftNavigationOnPLPEnabled: PropTypes.bool,
};

export default errorBoundary(withStyles(Carousel, CarouselStyle));
export { Carousel as CarouselVanilla };
